import { OrganizationComponents } from '@/api'
import { IOrganization } from '@/models'

/**
 * Preview orgs are temporary copies of an org's configuration, created from
 * Configuration Hub (organization_use "Preview", parent_production_org_id =
 * the org they copy). They are meant to be deleted once applied or abandoned.
 */
export const PREVIEW_ORG_TTL_DAYS = 7

const DAY_MS = 24 * 60 * 60 * 1000

export type PreviewCleanupState = 'active' | 'expired' | 'queued'

export interface PreviewCleanupRow {
  organization: IOrganization
  parentName?: string
  ageDays?: number
  state: PreviewCleanupState
  queuedAt?: string
  queuedBy?: string
}

export const isPreviewOrganization = (organization?: IOrganization) =>
  // organization-client-internal types don't know "Preview" yet
  (organization?.organization_use as string | null | undefined) === 'Preview'

/**
 * Every preview org with its cleanup state: queued (marked for deletion,
 * removed by the nightly cleanup job), expired (older than the TTL and not
 * queued yet) or active. Sorted queued last, then oldest first.
 */
export const buildPreviewCleanupRows = ({
  organizations,
  cleanupQueue,
  now = Date.now(),
  ttlDays = PREVIEW_ORG_TTL_DAYS
}: {
  organizations: IOrganization[]
  cleanupQueue: OrganizationComponents.Schemas.OrganizationToCleanup[]
  now?: number
  ttlDays?: number
}): PreviewCleanupRow[] => {
  const namesById = new Map(
    organizations.map((org) => [String(org.id), org.name ?? undefined])
  )
  const queueById = new Map(
    cleanupQueue.map((entry) => [String(entry.org_id), entry])
  )

  const rows = organizations
    .filter(isPreviewOrganization)
    .map((organization): PreviewCleanupRow => {
      const created = Date.parse(organization.created_date)
      const ageDays = Number.isNaN(created)
        ? undefined
        : Math.floor((now - created) / DAY_MS)
      const queued = queueById.get(String(organization.id))
      const state: PreviewCleanupState = queued
        ? 'queued'
        : ageDays !== undefined && ageDays >= ttlDays
        ? 'expired'
        : 'active'

      return {
        organization,
        parentName: organization.parent_production_org_id
          ? namesById.get(String(organization.parent_production_org_id))
          : undefined,
        ageDays,
        state,
        queuedAt: queued?.deleted_at,
        queuedBy: queued?.deleted_by
      }
    })

  const stateOrder: Record<PreviewCleanupState, number> = {
    expired: 0,
    active: 1,
    queued: 2
  }

  return rows.sort(
    (a, b) =>
      stateOrder[a.state] - stateOrder[b.state] ||
      (b.ageDays ?? -1) - (a.ageDays ?? -1)
  )
}
